// client/src/components/common/UnifiedDateRangePicker.tsx (converted from .js, ST-00204)
//
// The shared date-range picker (CLAUDE.md: every date selection goes through it).
//
// One picker, two containers (PickerSurface): nothing reaches the page until Apply; Cancel, Esc
// and a click outside discard the pending range. `mode="day"` picks one day (Plan Board) and
// hands it out as both start and end.
//
// Desktop (ST-00294): the trigger is the compact toolbar field it always was (36 px, the range
// as YYYY/MM/DD – YYYY/MM/DD; text that does not fit its column is cut off with "…" instead of
// running over the border, ST-00246). It opens a popup anchored under the field (PickerPopover):
// the quick ranges for the bounds down the left (looking back for 'past', ahead for 'future'),
// Start / End text boxes on top — typed as 2026/09/01, 2026-09-01, 01/09/2026 or 01-09-2026,
// with the same messages as ever (future / past days, an end before the start), Enter applies —
// and two months side by side under them (one where the window is too narrow; one for
// `mode="day"`). Focusing a box makes that end the one a calendar click sets, as the phone's
// Start / End tabs do. It opens with the Start text selected, ready to type over.
//
// Phone / touch (ST-00204, usePickerSheetMode): the trigger is full width and 44 px tall, and it
// opens a bottom sheet (PickerSheet) with Start / End tabs, preset chips and one month of 44 px
// days — no text boxes, so the keyboard never opens.
//
// Both commit only the ends that moved, through useSequencedRangeCommit (the end after the page
// has taken the start), so pages that build each update from their render-time state keep both.
//
// `onOpenChange` (ST-00221, AMD-30) reports the popup opening and closing — after the range it
// handed over — so a page that loads on a date change can wait for the whole range instead of
// reloading on the start date alone.

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import PickerSurface from './PickerSurface';
import PickerCalendar from './PickerCalendar';
import PickerPresets from './PickerPresets';
import PickerTypedField, { type TypedCheck } from './PickerTypedField';
import {
  useLatest,
  usePickerSheetMode,
  useSequencedRangeCommit,
  useViewportWidthAtLeast,
} from './pickerHooks';
import {
  blockedAbove,
  blockedBelow,
  dayKey,
  dayPresets,
  firstMonthFor,
  formatPickerDate,
  formatPickerDay,
  formatTypedDate,
  monthShown,
  parseTypedDate,
  pickRangeDay,
  rangePresets,
  sameDay,
  startOfDay,
  startOfMonth,
  toDateOrToday,
  type PickerBounds,
  type RangePreset,
  type RangeState,
  type RangeStep,
} from './pickerLogic';

/** What the pages pass: a Date, a date string, or nothing (reads as today). */
export type PickerDateValue = Date | string | null | undefined;

export interface UnifiedDateRangePickerProps {
  startDate: PickerDateValue;
  endDate: PickerDateValue;
  onStartDateChange: (d: Date) => void;
  onEndDateChange: (d: Date) => void;
  disabled?: boolean;
  /** Unsaved changes on the page: amber highlight on the trigger. */
  hasPendingChanges?: boolean;
  /** 'past' (default) = today and earlier, 'future' = today and later, 'all' = unbounded. */
  bounds?: PickerBounds;
  /** 'day': the picker picks one day and hands it out as both start and end. */
  mode?: 'range' | 'day';
  /** The desktop popup / phone sheet opened (true) or closed (false). Not called on mount. */
  onOpenChange?: (open: boolean) => void;
}

/** Below this window width the desktop popup shows one month (two need about 760 px). */
export const TWO_MONTHS_MIN_WIDTH = 800;

const CalendarIcon = ({ className }: { className: string }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M5 10h10M12 7l3 3-3 3"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** YYYY/MM/DD from the local calendar date — the desktop trigger's format. */
const formatDate = (value: PickerDateValue): string => formatTypedDate(toDateOrToday(value));

const UnifiedDateRangePicker = ({
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  disabled = false,
  hasPendingChanges = false,
  bounds = 'past',
  mode = 'range',
  onOpenChange,
}: UnifiedDateRangePickerProps) => {
  const sheetMode = usePickerSheetMode();
  const wide = useViewportWidthAtLeast(TWO_MONTHS_MIN_WIDTH);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const activeDayRef = useRef<HTMLButtonElement | null>(null);
  const firstFieldRef = useRef<HTMLInputElement | null>(null);
  // The pending range: nothing is committed until Apply.
  const [pending, setPending] = useState<RangeState>({
    start: new Date(),
    end: new Date(),
    step: 'start',
  });
  const [month, setMonth] = useState<Date>(() => startOfMonth(new Date()));
  // Desktop text boxes holding something that cannot be taken: Apply stays off.
  const [invalid, setInvalid] = useState<Record<RangeStep, boolean>>({ start: false, end: false });

  const commit = useSequencedRangeCommit<Date>(onStartDateChange, onEndDateChange);

  const start = toDateOrToday(startDate);
  const end = toDateOrToday(endDate);
  const months = !sheetMode && mode === 'range' && wide ? 2 : 1;
  const limits = { bounds };

  const open = isDatePickerOpen && !disabled;

  // Report opening / closing; declared after useSequencedRangeCommit, so on Apply the queued
  // end date reaches the page before the close does.
  const onOpenChangeRef = useLatest(onOpenChange);
  const reportedOpen = useRef(false);
  useEffect(() => {
    if (reportedOpen.current === open) return;
    reportedOpen.current = open;
    onOpenChangeRef.current?.(open);
  }, [open, onOpenChangeRef]);

  // Rotating a phone across the breakpoint while open: close instead of switching popups.
  useEffect(() => {
    setIsDatePickerOpen(false);
  }, [sheetMode]);

  const close = () => setIsDatePickerOpen(false);

  // Primed in the same render that opens the picker (no stale first frame).
  const handleOpenDatePicker = () => {
    if (disabled) return;
    const s = startOfDay(start);
    const e = startOfDay(end);
    setPending({ start: s, end: dayKey(e) < dayKey(s) ? s : e, step: 'start' });
    setMonth(firstMonthFor(s, months, limits));
    setInvalid({ start: false, end: false });
    setIsDatePickerOpen(true);
  };

  const presets = useMemo(
    () => (mode === 'day' ? dayPresets(bounds) : rangePresets(bounds)),
    // Recomputed per opening so "today" is fresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mode, bounds, isDatePickerOpen]
  );

  /** The calendar turns to `day` when it is not already on show. */
  const reveal = (day: Date) =>
    setMonth((m) => (monthShown(day, m, months) ? m : firstMonthFor(day, months, limits)));

  const pickDay = (day: Date) => {
    if (mode === 'day') setPending({ start: startOfDay(day), end: startOfDay(day), step: 'start' });
    else setPending((p) => pickRangeDay(p, day));
  };

  const pickPreset = (p: RangePreset) => {
    setPending({ start: p.start, end: p.end, step: 'start' });
    const now = new Date();
    const coversToday = dayKey(p.start) <= dayKey(now) && dayKey(now) <= dayKey(p.end);
    const target = coversToday ? now : p.start;
    if (months === 1) setMonth(startOfMonth(target));
    else
      setMonth((m) =>
        monthShown(p.start, m, months) && monthShown(p.end, m, months)
          ? m
          : firstMonthFor(target, months, limits)
      );
  };

  // Phone: the Start / End tabs.
  const showEnd = (step: RangeStep) => {
    setPending((p) => ({ ...p, step }));
    setMonth(startOfMonth(step === 'start' ? pending.start : pending.end));
  };

  // Desktop: focusing a text box makes its end the one being set.
  const focusEnd = (step: RangeStep) => {
    setPending((p) => ({ ...p, step }));
    reveal(step === 'start' ? pending.start : pending.end);
  };

  // ── Desktop text boxes ────────────────────────────────────────────────────

  const checkTyped =
    (which: RangeStep) =>
    (text: string): TypedCheck => {
      if (!text.trim()) return { error: '' };
      const d = parseTypedDate(text);
      if (!d) return { error: 'Invalid date format', unreadable: true };
      if (bounds === 'past' && blockedAbove(d, { bounds })) return { error: 'Cannot select future dates' };
      if (bounds === 'future' && blockedBelow(d, { bounds })) return { error: 'Cannot select past dates' };
      // Calendar days, not instants (PLT-16, ST-00230): the start's own day is a valid end.
      if (which === 'end' && dayKey(d) < dayKey(pending.start)) {
        return { error: 'End date must be after start date' };
      }
      return { error: '' };
    };

  const typeDay =
    (which: RangeStep) =>
    (text: string): string | undefined => {
      const d = parseTypedDate(text);
      if (!d || checkTyped(which)(text).error) return undefined;
      if (mode === 'day') setPending({ start: d, end: d, step: 'start' });
      else if (which === 'start') {
        // A start after the end takes the end with it.
        setPending((p) => ({ start: d, end: dayKey(p.end) < dayKey(d) ? d : p.end, step: 'start' }));
      } else setPending((p) => ({ ...p, end: d, step: 'end' }));
      reveal(d);
      return formatTypedDate(d);
    };

  const markInvalid = (which: RangeStep) => (bad: boolean) =>
    setInvalid((cur) => (cur[which] === bad ? cur : { ...cur, [which]: bad }));

  const typedInvalid = !sheetMode && (invalid.start || invalid.end);

  const apply = () => {
    if (typedInvalid) return;
    const s = pending.start;
    const e = mode === 'day' ? pending.start : pending.end;
    // Only the ends that moved (an empty value on the page always counts as moved).
    commit(
      startDate && sameDay(s, start) ? undefined : s,
      endDate && sameDay(e, end) ? undefined : e
    );
    setIsDatePickerOpen(false);
  };

  const presetList = (
    <PickerPresets
      presets={presets}
      start={pending.start}
      end={mode === 'day' ? pending.start : pending.end}
      onPick={pickPreset}
      label={mode === 'day' ? 'Quick days' : 'Quick ranges'}
    />
  );

  const calendar = (
    <PickerCalendar
      month={month}
      onMonthChange={setMonth}
      months={months}
      selected={mode === 'day' ? pending.start : null}
      rangeStart={mode === 'range' ? pending.start : null}
      rangeEnd={mode === 'range' ? pending.end : null}
      bounds={bounds}
      onSelect={pickDay}
      activeDayRef={activeDayRef}
      showToday={!presets.some((p) => p.id === 'today')}
    />
  );

  const surface = (content: ReactNode) => (
    <PickerSurface
      variant={sheetMode ? 'sheet' : 'popover'}
      open={open}
      title={mode === 'day' ? 'Choose a day' : 'Select date range'}
      onCancel={close}
      onApply={apply}
      applyDisabled={typedInvalid}
      returnFocusRef={triggerRef}
      initialFocusRef={sheetMode ? activeDayRef : firstFieldRef}
    >
      {content}
    </PickerSurface>
  );

  if (sheetMode) {
    const sheetLabel =
      mode === 'day'
        ? formatPickerDay(start)
        : `${formatPickerDate(start)} – ${formatPickerDate(end)}`;
    return (
      <div className="relative date-range-container">
        <div
          className={`picker-field${hasPendingChanges ? ' picker-field--pending' : ''}${disabled ? ' picker-field--disabled' : ''}`}
        >
          <button
            ref={triggerRef}
            type="button"
            className="picker-field__main"
            onClick={handleOpenDatePicker}
            disabled={disabled}
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-label={
              mode === 'day'
                ? `Day: ${formatPickerDay(start)}. Change the day`
                : `Dates: ${formatPickerDate(start)} to ${formatPickerDate(end)}. Change the dates`
            }
          >
            <span className="picker-field__text">{sheetLabel}</span>
            <CalendarIcon className="picker-field__icon" />
          </button>
        </div>
        {surface(
          <>
            {mode === 'range' ? (
              <>
                <div className="picker-ends" role="group" aria-label="Range">
                  {(['start', 'end'] as const).map((step) => (
                    <button
                      key={step}
                      type="button"
                      className="picker-end"
                      aria-pressed={pending.step === step}
                      aria-label={`${step === 'start' ? 'Start' : 'End'}: ${formatPickerDate(step === 'start' ? pending.start : pending.end)}`}
                      onClick={() => showEnd(step)}
                    >
                      <span className="picker-end__label">{step === 'start' ? 'Start' : 'End'}</span>
                      <span className="picker-end__value">
                        {formatPickerDate(step === 'start' ? pending.start : pending.end)}
                      </span>
                    </button>
                  ))}
                </div>
                <p className="picker-hint" aria-live="polite">
                  {pending.step === 'start' ? 'Tap the first day.' : 'Now tap the last day.'}
                </p>
              </>
            ) : (
              <p className="picker-hint picker-hint--value" aria-live="polite">
                {formatPickerDay(pending.start)}
              </p>
            )}
            {presetList}
            {calendar}
          </>
        )}
      </div>
    );
  }

  const rangeText = `${formatDate(start)} – ${formatDate(end)}`;
  return (
    <div className="relative date-range-container">
      {/* Combined Date Range Display */}
      <button
        ref={triggerRef}
        type="button"
        onClick={handleOpenDatePicker}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={rangeText}
        className={`w-full flex items-center justify-between gap-2 px-2.5 py-[7px] border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400 transition-all duration-200 min-w-0 ${
          disabled
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
            : hasPendingChanges
              ? 'border-amber-400 bg-amber-50 cursor-pointer hover:border-amber-500'
              : 'border-slate-300 bg-surface cursor-pointer hover:border-slate-400'
        }`}
      >
        {/* Cut off with "…", never over the border, where the column is narrower than the range
            (ST-00246); the title shows the whole range. */}
        <div className="flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-left text-slate-700">
          <span className="text-sm text-slate-700">{formatDate(start)}</span>
          <span className="text-slate-300 mx-1.5">&ndash;</span>
          <span className="text-sm text-slate-700">{formatDate(end)}</span>
        </div>
        <CalendarIcon className="w-4 h-4 text-slate-400 flex-shrink-0 ml-1" />
      </button>

      {surface(
        <div className="picker-range">
          {presetList}
          <div className="picker-range__main">
            {mode === 'range' ? (
              <>
                <div className="picker-typed-row" role="group" aria-label="Range">
                  <PickerTypedField
                    label="Start"
                    value={formatTypedDate(pending.start)}
                    placeholder="YYYY/MM/DD"
                    active={pending.step === 'start'}
                    check={checkTyped('start')}
                    onType={typeDay('start')}
                    onFocus={() => focusEnd('start')}
                    onEnter={apply}
                    onInvalidChange={markInvalid('start')}
                    inputRef={firstFieldRef}
                  />
                  <span className="picker-typed-row__arrow" aria-hidden="true">
                    <ArrowIcon />
                  </span>
                  <PickerTypedField
                    label="End"
                    value={formatTypedDate(pending.end)}
                    placeholder="YYYY/MM/DD"
                    active={pending.step === 'end'}
                    check={checkTyped('end')}
                    onType={typeDay('end')}
                    onFocus={() => focusEnd('end')}
                    onEnter={apply}
                    onInvalidChange={markInvalid('end')}
                  />
                </div>
                <p className="picker-hint" aria-live="polite">
                  {pending.step === 'start' ? 'Click the first day.' : 'Now click the last day.'}
                </p>
              </>
            ) : (
              <div className="picker-typed-row">
                <PickerTypedField
                  label="Day"
                  value={formatTypedDate(pending.start)}
                  placeholder="YYYY/MM/DD"
                  check={checkTyped('start')}
                  onType={typeDay('start')}
                  onEnter={apply}
                  onInvalidChange={markInvalid('start')}
                  inputRef={firstFieldRef}
                />
              </div>
            )}
            {calendar}
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedDateRangePicker;
